import { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import SearchSection from '../components/SearchSection';
import SearchResult from '../components/SearchResult';
import ExamTable from '../components/ExamTable';
import Footer from '../components/Footer';
import WelcomeModal from '../components/WelcomeModal';
import { useExamSchedule } from '../hooks/useExamSchedule';
import RoomAvailability from '../components/RoomAvailability';
import { parseExamDate, isSameDay } from '../utils/dateUtils';
import NearbyRoomsModal from '../components/NearbyRoomsModal';

function MainPage() {
  // Add state for welcome modal
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  
  // Existing state...
  const [building, setBuilding] = useState('');
  const [room, setRoom] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [selectedDate, setSelectedDate] = useState(''); // New state for date selection
  
  // Table search and filtering state
  const [courseSearch, setCourseSearch] = useState('');
  const [buildingSearch, setBuildingSearch] = useState('');
  const [roomSearch, setRoomSearch] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Get exam data from custom hook
  const { examSchedule, isLoading, error, validationResult, termLabel } = useExamSchedule();
  
  // Get room availability utilities with date parameter
  const { isSearching, checkAvailability } = RoomAvailability({
    examSchedule,
    building,
    room,
    onResultUpdate: setSearchResult,
    selectedDate
  });
  
  // Status color determination
  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (date, time) => {
    const now = new Date();
    const examDate = parseExamDate(date);
    
    // If not today or date couldn't be parsed, show as available
    if (!examDate || !isSameDay(examDate, now)) {
      return "success"; 
    }
    
    // For today, check if the exam time has passed
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Parse exam time
    const examTime = time.split(' - ')[0]; // Get start time
    const [timeStr, period] = examTime.split(' ');
    const [hourStr, minuteStr] = timeStr.split(':');
    let examHour = parseInt(hourStr);
    const examMinutes = parseInt(minuteStr);
    
    // Convert to 24-hour format
    if (period === 'PM' && examHour < 12) examHour += 12;
    if (period === 'AM' && examHour === 12) examHour = 0;
    
    // If exam is in the past, room is available
    if (examHour < currentHour || 
        (examHour === currentHour && examMinutes < currentMinutes)) {
      return "success";
    }
    
    // Otherwise, room is occupied (or will be soon)
    return "danger";
  };
  
  // Filter exams based on search term, date, and availability
  const filteredExams = useMemo(() => {
    return examSchedule.filter(exam => {
      // Course search - make it more flexible
      const searchInput = courseSearch.toLowerCase().trim();
      const courseText = exam.course.toLowerCase();
      
      // Check for exact match
      const matchesCourse = !searchInput || courseText.includes(searchInput);
      
      // If no match, try alternate format (with/without spaces)
      const alternateSearch = searchInput.replace(/\s+/g, '');
      const alternateText = courseText.replace(/\s+/g, '');
      const matchesAlternate = alternateSearch && alternateText.includes(alternateSearch);
      
      // Building search
      const matchesBuilding = !buildingSearch || 
        exam.building.toLowerCase().includes(buildingSearch.toLowerCase().trim());
      
      // Room search
      const matchesRoom = !roomSearch || 
        exam.room.toLowerCase().includes(roomSearch.toLowerCase().trim());
      
      // Date filter
      const matchesDate = !filterDate || exam.date === filterDate;
      
      // Combine all filters with AND logic
      return (matchesCourse || matchesAlternate) && matchesBuilding && matchesRoom && matchesDate;
    });
  }, [examSchedule, courseSearch, buildingSearch, roomSearch, filterDate]);

  // Calculate pagination
  const paginatedExams = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredExams.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredExams, currentPage, itemsPerPage]);

  // Handle page changes
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Update your state setters to reset searchResult when inputs change

  // First, create modified setter functions
  const handleBuildingChange = (value) => {
    setBuilding(value);
    // Reset search result when building changes
    setSearchResult(null);
  };

  const handleRoomChange = (value) => {
    setRoom(value);
    // Reset search result when room changes
    setSearchResult(null);
  };
  
  const handleDateChange = (value) => {
    setSelectedDate(value);
    setSearchResult(null);
  };
  
  // Get unique dates for the date selector
  const availableDates = useMemo(() => {
    return [...new Set(examSchedule.map(exam => exam.date))].sort();
  }, [examSchedule]);

  // Off-season mode removed; always show main UI

  // Check if it's the first visit
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    if (!hasSeenWelcome) {
      setShowWelcomeModal(true);
    }
  }, []);

  // Handle closing the welcome modal
  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    localStorage.setItem('hasSeenWelcome', 'true');
  };

  // Add this state to track nearby room selections
  const [nearbyRoomSelected, setNearbyRoomSelected] = useState(false);

  // Modify the handleNearbyRoomSelect function
  const handleNearbyRoomSelect = (buildingId, roomId) => {
    // First, clear the search result completely
    setSearchResult(null);
    
    // Then update building and room in sequence
    setBuilding(buildingId);
    setRoom(roomId);
    
    // Set date to empty string which defaults to "today" in the availability check
    setSelectedDate('');
    
    // Set flag indicating we've selected a nearby room
    setNearbyRoomSelected(true);
  };

  // Add this effect to handle checking availability after nearby room selection
  useEffect(() => {
    if (nearbyRoomSelected && building && room && examSchedule && examSchedule.length > 0) {
      // Wait for states to be properly updated
      const timer = setTimeout(() => {
        checkAvailability();
        setNearbyRoomSelected(false); // Reset flag after checking
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [nearbyRoomSelected, building, room, examSchedule, checkAvailability]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <Header onOpenWelcomeModal={() => setShowWelcomeModal(true)} />
      
      {/* Welcome Modal */}
      <WelcomeModal 
        isOpen={showWelcomeModal} 
        onClose={handleCloseWelcomeModal} 
      />
      
  <main className="max-w-4xl mx-auto px-6 pt-6 pb-20 text-gray-800 dark:text-gray-200">
        {isLoading ? (
          <div className="text-center py-12">
    <p className="text-gray-500 dark:text-gray-400">Loading exam schedule data...</p>
          </div>
        ) : error ? (
      <div className="bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 p-4 rounded-lg mb-8 border border-red-100 dark:border-red-900/40">
            <p>{error}</p>
            
          </div>
  ) : (
          <>
            <SearchSection 
              building={building}
              setBuilding={handleBuildingChange}  // Use the new handler
              room={room}
              setRoom={handleRoomChange}  // Use the new handler
              checkAvailability={checkAvailability}
              isSearching={isSearching}
              selectedDate={selectedDate}
              setSelectedDate={handleDateChange}
              availableDates={availableDates}
              termLabel={termLabel}
            />
            
            {/* Replace NearbyRooms with NearbyRoomsModal */}
            <NearbyRoomsModal 
              examSchedule={examSchedule} 
              onRoomSelect={handleNearbyRoomSelect} 
            />
            
            <SearchResult 
              searchResult={searchResult}
              building={building}
              room={room}
            />

            <ExamTable 
              exams={paginatedExams}
              courseSearch={courseSearch}
              setCourseSearch={setCourseSearch}
              buildingSearch={buildingSearch}
              setBuildingSearch={setBuildingSearch}
              roomSearch={roomSearch}
              setRoomSearch={setRoomSearch}
              filterDate={filterDate}
              setFilterDate={setFilterDate}
              showOnlyAvailable={showOnlyAvailable}
              setShowOnlyAvailable={setShowOnlyAvailable}
              currentPage={currentPage}
              totalPages={Math.ceil(filteredExams.length / itemsPerPage)}
              onPageChange={handlePageChange}
              allExams={examSchedule} // Add this new prop
            />
          </>
  )}
      </main>

      <Footer />
    </div>
  );
}

export default MainPage;